<script setup>
/**
 * Fork overlay (new file, no OSS equivalent) — one Eva turn in the copilot feed.
 * Mirrors the upstream Captain CopilotAssistantMessage pattern (label row, sanitized markdown body)
 * with Eva identity and EVA_COPILOT copy (pt-BR ships in fork.json — Captain's copilot has no pt-BR).
 * When the reply carries a reply_suggestion, it renders as a distinct card whose action inserts the
 * DRAFT (not Eva's whole answer) into the reply editor, rich or plain depending on the inbox type.
 */
import { computed } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  message: { type: Object, required: true },
  conversationInboxType: { type: String, default: '' },
});

const hasEmptyContent = computed(() => !props.message?.content);

const messageContent = computed(
  () => new MessageFormatter(props.message.content).formattedMessage
);

const replySuggestion = computed(() => props.message?.reply_suggestion);

const useReply = () => {
  const targetEvent = [INBOX_TYPES.WEB, INBOX_TYPES.EMAIL].includes(
    props.conversationInboxType
  )
    ? BUS_EVENTS.INSERT_INTO_RICH_EDITOR
    : BUS_EVENTS.INSERT_INTO_NORMAL_EDITOR;
  emitter.emit(targetEvent, replySuggestion.value);
};
</script>

<template>
  <div class="flex flex-col gap-1 text-n-slate-12">
    <div class="flex items-center gap-1.5 font-medium">
      <i class="i-lucide-sparkles size-3.5 text-n-iris-9" />
      {{ $t('EVA_COPILOT.NAME') }}
    </div>
    <span v-if="hasEmptyContent" class="text-n-ruby-11">
      {{ $t('EVA_COPILOT.EMPTY_MESSAGE') }}
    </span>
    <div
      v-else
      v-dompurify-html="messageContent"
      class="prose-sm break-words"
    />
    <div
      v-if="!hasEmptyContent && replySuggestion"
      data-test-id="eva-reply-suggestion"
      class="mt-1 space-y-2 rounded-lg border border-n-weak bg-n-alpha-3 px-3 py-2"
    >
      <p class="mb-0 text-xs text-n-slate-10">
        {{ $t('EVA_COPILOT.SUGGESTION_LABEL') }}
      </p>
      <p class="mb-0 break-words text-sm text-n-slate-12">
        {{ replySuggestion }}
      </p>
      <Button :label="$t('EVA_COPILOT.USE')" faded sm slate @click="useReply" />
    </div>
  </div>
</template>
